import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {openNotificationError, openNotificationSuccess} from "../utils/Utils";

export const useUploadHandler = (uploadFn, options = {}) => {
    const {
        debounceMs = 500,
        onSuccess,
        onError,
        extraFields = {},
    } = options;

    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (!fileList.length) return;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => handleUpload(fileList), debounceMs);
        return () => clearTimeout(timeoutRef.current);
    }, [fileList]);

    const handleUpload = async (files) => {
        if (uploading || !files.length) return;
        setUploading(true);

        try {
            const requests = await Promise.all(
                files.map(async (fileItem) => {
                    const file = fileItem.originFileObj;
                    if (!file) return null;

                    const uid = uuidv4();

                    const previewUrl = URL.createObjectURL(file);
                    const { width, height, frameCount } = await new Promise((resolve, reject) => {
                        const img = new Image();
                        img.src = previewUrl;
                        img.onload = () => {
                            const w = img.width;
                            const h = img.height;
                            const fCount = Math.max(1, Math.floor(w / h));
                            URL.revokeObjectURL(previewUrl);
                            resolve({ width: w, height: h, frameCount: fCount });
                        };
                        img.onerror = reject;
                    });

                    const base64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });

                    return {
                        requestUuid: uid,
                        fileName: file.name,
                        base64,
                        width,
                        height,
                        frameCount,
                        ...extraFields,
                    };
                })
            );

            const validRequests = requests.filter(Boolean);
            if (!validRequests.length) return;

            const result = await uploadFn(validRequests);
            if (result?.status === 200) {
                openNotificationSuccess(`Uploaded ${validRequests.length} file(s) successfully!`);
                onSuccess?.(result);
                setFileList([]);
            } else {
                openNotificationError("Upload failed");
                onError?.(result);
            }
        } catch (err) {
            console.error("Upload failed:", err);
            openNotificationError("Upload failed");
            onError?.(err);
        } finally {
            setUploading(false);
        }
    };

    return {
        fileList,
        setFileList,
        uploading,
    };
};
